import { AnimatedContentLoader, Button, Input, ModalV2, TextArea } from '@components'
import { isVATObliged, useBusinessContext } from '@context'
import { IBusiness } from '@query'
import { useCountries } from '@root/query/hooks'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaFileInvoice, FaSave } from 'react-icons/fa'
import styled from 'styled-components'
import { SettingsReadOnlyField } from '@pages/SettingsPage/components/SettingsReadOnlyField'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { SettingsDataCard } from '@pages/SettingsPage/components/SettingsDataCard'
import {
  FormSectionHeading,
  getInvoicingBasicsRequiredDefaultValues,
  InvoicingBasicsRequiredFields
} from './InvoicingBasicsRequiredFields'
import { useInvoicingBasicsForm } from './useInvoicingBasicsForm'

const getDefaultValues = (data?: Partial<IBusiness>) => ({
  ...getInvoicingBasicsRequiredDefaultValues(data),
  invoicing_default_email_subject: data?.invoicing_default_email_subject,
  invoicing_default_email_content: data?.invoicing_default_email_content,
  invoicing_vat_exemption_reason: data?.invoicing_vat_exemption_reason
})

const InvoiceSettingsGeneral: React.FC = () => {
  // React Hook Form mutates `formState.errors` in place. The compiler then keeps the previous inputs.
  'use no memo'
  const { data } = useBusinessContext()
  const [t] = useTranslation()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { data: countries = [] } = useCountries()
  const invoicingCountryName =
    countries.find(country => country.code === data?.invoicing_country)?.name ||
    data?.invoicing_country

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
    updateMutation
  } = useInvoicingBasicsForm({
    getDefaultValues,
    onSuccess: () => {
      toast.success(t('general.changesSaved'))
      setIsModalVisible(false)
    }
  })

  return (
    <SettingsSubPageLayout title={t('settings.sections.invoicingBasics')} contentPadding="none">
      <Content>
        <AnimatedContentLoader isLoading={!data}>
          <SettingsDataCard
            icon={<FaFileInvoice />}
            title={t('settings.sections.invoicingBasics')}
            action={
              <Button
                isSecondary
                size="sm"
                onClick={() => {
                  reset(getDefaultValues(data))
                  setIsModalVisible(true)
                }}
              >
                {t('general.edit')}
              </Button>
            }
          >
            <CardSection>
              <CardSectionHeading>{t('settings.invoicing.paymentTitle')}</CardSectionHeading>
              <CardSectionFields>
                <SettingsReadOnlyField
                  label={t('settings.invoicing.iban')}
                  value={data?.invoicing_iban}
                />
                <SettingsReadOnlyField
                  label={t('settings.invoicing.bic')}
                  value={data?.invoicing_bic}
                />
              </CardSectionFields>
            </CardSection>

            <CardSection>
              <CardSectionHeading>
                {t('settings.invoicing.invoicingAddressTitle')}
              </CardSectionHeading>
              <CardSectionFields>
                <SettingsReadOnlyField
                  label={t('general.address.email')}
                  value={data?.invoicing_email}
                />
                <SettingsReadOnlyField
                  label={t('settings.invoicing.contactPerson')}
                  value={data?.invoicing_contact}
                />
                <SettingsReadOnlyField
                  label={t('general.address.street')}
                  value={data?.invoicing_street}
                />
                <SettingsReadOnlyField
                  label={t('general.address.postalCode')}
                  value={data?.invoicing_postal_code}
                />
                <SettingsReadOnlyField
                  label={t('general.address.city')}
                  value={data?.invoicing_city}
                />
                <SettingsReadOnlyField
                  label={t('general.address.country')}
                  value={invoicingCountryName}
                />
              </CardSectionFields>
            </CardSection>

            <CardSection>
              <CardSectionHeading>{t('settings.invoicing.emailDefaultsTitle')}</CardSectionHeading>
              <CardSectionFields>
                <SettingsReadOnlyField
                  label={t('settings.invoicing.defaultEmailSubject')}
                  value={data?.invoicing_default_email_subject}
                />
                <SettingsReadOnlyField
                  label={t('settings.invoicing.defaultEmailBody')}
                  value={data?.invoicing_default_email_content}
                />
              </CardSectionFields>
            </CardSection>

            {data && !isVATObliged(data) && (
              <CardSection>
                <CardSectionHeading>{t('settings.invoicing.vatExemptionTitle')}</CardSectionHeading>
                <CardSectionFields>
                  <SettingsReadOnlyField
                    label={t('settings.invoicing.vatExemptionReason')}
                    value={data?.invoicing_vat_exemption_reason}
                  />
                </CardSectionFields>
              </CardSection>
            )}
          </SettingsDataCard>
        </AnimatedContentLoader>

        <ModalV2
          header={t('settings.sections.invoicingBasics')}
          isVisible={isModalVisible}
          handleOnClose={() => setIsModalVisible(false)}
          width={500}
          footer={
            <ModalFooter>
              <Button type="button" onClick={() => setIsModalVisible(false)}>
                {t('general.cancel')}
              </Button>
              <Button
                type="submit"
                form="invoice-settings-general-form"
                intent="success"
                icon={<FaSave />}
                disabled={!isDirty || updateMutation.isPending}
                showSpinner={updateMutation.isPending}
              >
                {t('general.save')}
              </Button>
            </ModalFooter>
          }
        >
          <form
            id="invoice-settings-general-form"
            onSubmit={handleSubmit(values => updateMutation.mutate(values))}
          >
            <InvoicingBasicsRequiredFields
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
            />

            <FormSectionHeading>{t('settings.invoicing.emailDefaultsTitle')}</FormSectionHeading>
            <Input
              label={t('settings.invoicing.defaultEmailSubject')}
              info={t('settings.invoicing.defaultEmailSubjectInfo')}
              errors={errors.invoicing_default_email_subject}
              {...register('invoicing_default_email_subject')}
            />
            <TextArea
              label={t('settings.invoicing.defaultEmailBody')}
              info={t('settings.invoicing.defaultEmailBodyInfo')}
              errors={errors.invoicing_default_email_content}
              rows={8}
              {...register('invoicing_default_email_content')}
            />

            {data && !isVATObliged(data) && (
              <>
                <FormSectionHeading>{t('settings.invoicing.vatExemptionTitle')}</FormSectionHeading>
                <TextArea
                  label={t('settings.invoicing.vatExemptionReason')}
                  info={t('settings.invoicing.vatExemptionReasonInfo')}
                  errors={errors.invoicing_vat_exemption_reason}
                  rows={4}
                  {...register('invoicing_vat_exemption_reason')}
                />
              </>
            )}
          </form>
        </ModalV2>
      </Content>
    </SettingsSubPageLayout>
  )
}

const Content = styled.div`
  margin: 1rem;
`

const CardSection = styled.section`
  display: grid;
  gap: ${({ theme }) => theme.spacing.sm}rem;

  & + & {
    margin-top: ${({ theme }) => theme.spacing.md}rem;
  }
`

const CardSectionFields = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const CardSectionHeading = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.metalGray};

  &::after {
    content: '';
    height: 1px;
    flex: 1;
    background: ${({ theme }) => theme.colors.neutralGray};
    opacity: 0.7;
  }
`

const ModalFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export default InvoiceSettingsGeneral
